<template>
    <div
        id="searchBar"
        ref="searchBar"
        tabindex="0"
        class="search-bar row mx-0 mb-3"
        :class="{ 'search-bar--with-dates': showDateSearchFields }"
    >
        <Autocomplete
            :placeholder="$t('SearchBar.autocomplete.placeholder')"
            class="d-none d-lg-flex col-lg-3 search-bar__section search-bar__filter-option search-bar__autocomplete"
            :value.sync="query"
            clearable
        />

        <template v-if="showDateSearchFields">
            <div class="search-bar__section search-bar__filter-option search-bar__from col-6 col-sm-3 col-md-auto">
                <Datepicker
                    :value="from"
                    @change="updateFrom($event)"
                    :available-dates="availableFromDates"
                    :placeholder="$t('DateSearch.searchBar.from')"
                    :aria-label="$t('DateSearch.searchBar.from')"
                    input-id="searchBarFrom"
                    class="search-bar__input"
                    :class="{ 'datepicker--empty': !from }"
                    allow-clear
                >
                    <template v-if="from" #pre>
                        <label for="searchBarFrom" class="datepicker__label">{{
                            $t('DateSearch.searchBar.from')
                        }}</label>
                    </template>
                </Datepicker>
            </div>
            <div class="search-bar__section search-bar__filter-option search-bar__until col-6 col-sm-3 col-md-auto">
                <Datepicker
                    :value="until"
                    @change="updateUntil($event)"
                    :available-dates="availableUntilDates"
                    :placeholder="$t('DateSearch.searchBar.until')"
                    :aria-label="$t('DateSearch.searchBar.until')"
                    input-id="searchBarUntil"
                    class="search-bar__input"
                    :class="{ 'datepicker--empty': !until }"
                    allow-clear
                >
                    <template v-if="until" #pre>
                        <label for="searchBarUntil" class="datepicker__label">{{
                            $t('DateSearch.searchBar.until')
                        }}</label>
                    </template>
                </Datepicker>
            </div>
        </template>

        <FilterDropdown
            :placeholder="$t('SearchBar.duration.placeholder')"
            :optionGroups="durationGroups"
            :selected.sync="durationSelected"
            :show-headers="showDateSearchFields"
            class="col-12 search-bar__section search-bar__filter-option search-bar__duration"
            :class="[showDateSearchFields ? 'col-sm-6 col-md-3' : 'col-sm-4 col-lg-3']"
        >
            <IconSvg filepath="calendar.svg" slot="prefix" />
        </FilterDropdown>

        <FilterDropdown
            :placeholder="$t('SearchBar.more.placeholder')"
            :optionGroups="otherFilterGroups"
            :selected.sync="otherSelected"
            class="col-12 search-bar__section search-bar__filter-option search-bar__other-option pr-sm-0"
            :class="[showDateSearchFields ? 'col-sm-9 col-md-4 col-lg-2' : 'col-sm-4 col-lg-3']"
            show-headers
        >
            <IconSvg filepath="search/filter.svg" slot="prefix" />
        </FilterDropdown>

        <div
            class="col-12 search-bar__section search-bar__section-remaining pl-sm-0 justify-center"
            :class="[showDateSearchFields ? 'col-sm-3 col-md-2 col-lg-auto' : 'col-sm-4 col-lg-3']"
        >
            <button
                type="button"
                @click.prevent="submitSearch(false)"
                class="btn btn-primary btn-lg search-bar__submit-button"
            >
                <IconSvg filepath="search/magnifier.svg" />
                <span class="search-bar__button-text">
                    {{ $t('SearchBar.button.search.text') }}
                </span>
            </button>

            <template v-if="!isMapRoute">
                <button
                    class="search-bar__map-hint d-none d-xl-block"
                    @click="submitSearch(true)"
                    key="searchBarButton"
                >
                    {{ $t('SearchBar.button.mapSearch.hint') }}
                </button>

                <button
                    type="button"
                    @click.prevent="submitSearch(true)"
                    class="btn btn-primary btn-lg search-bar__submit-button search-bar__map-search"
                >
                    <media-only desktop tag="span">
                        <img
                            id="mapMagnifier"
                            src="@/assets/icons/search/map-magnifier.png"
                            alt="Kartensuche"
                            class="img-fluid"
                        />
                    </media-only>
                    <media-only mobile tag="span">
                        <IconSvg filepath="search/map.svg" class="search-bar__icon-map-mobile" id="mapIconMobile" />
                    </media-only>
                </button>
            </template>
        </div>
    </div>
</template>

<script>
import Autocomplete from '@/components/search/Autocomplete';
import FilterDropdown from '@/components/search/FilterDropdown';
import Datepicker from '@/components/Datepicker.vue';

function formatDate(date) {
    return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(date);
}

/**
 * Converts dd.mm.yyyy to Date instance
 * @param {string} dateString
 * @returns {Date}
 */
function parseGermanShortDateString(dateString) {
    const [dd, mm, yyyy] = (dateString || '').split('.');
    return new Date(`${yyyy}-${mm}-${dd}`);
}

/**
 * Add specified amount fo days to the date provided
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
function addDays(date, days) {
    const dateWithDaysAdded = new Date(date); //prevents date param mutation
    dateWithDaysAdded.setDate(dateWithDaysAdded.getDate() + days);
    return dateWithDaysAdded;
}

function generateDateList(startDate, numberOfDays) {
    let result = [formatDate(startDate)];
    for (let i = 1; i < numberOfDays; i++) {
        result.push(formatDate(addDays(startDate, i)));
    }
    return result;
}

export default {
    name: 'SearchBar',
    components: {
        Datepicker,
        Autocomplete,
        FilterDropdown
    },
    data() {
        const $t = this.$t;
        return {
            query: this.$route.query.q,
            term: {},
            from: this.$route.query.from || '',
            until: this.$route.query.until || '',
            initialDate: addDays(new Date(), 1), // initial date before selection is done
            initialSelectionRangeInDays: 365,
            durationSelected: [],
            durationGroups: [
                {
                    header: $t('Generic.duration'),
                    options: [
                        { text: $t('SearchBar.duration.day1'), value: 'day1' },
                        { text: $t('SearchBar.duration.day2'), value: 'day2' },
                        { text: $t('SearchBar.duration.day3'), value: 'day3' },
                        { text: $t('SearchBar.duration.day4'), value: 'day4' },
                        { text: $t('SearchBar.duration.day5'), value: 'day5' },
                        { text: $t('SearchBar.duration.day6'), value: 'day6' },
                        { text: $t('SearchBar.duration.day7_to_13'), value: 'day7_to_13' },
                        { text: $t('SearchBar.duration.day14_and_more'), value: 'day14_and_more' }
                    ]
                }
            ],
            otherFilterGroups: [
                {
                    header: $t('AdditionalHotelInfo.suitableFor'),
                    options: [
                        { text: $t('SearchBar.more.suitable_for_couples'), value: 'suitable_for_couples' },
                        { text: $t('SearchBar.more.suitable_for_singles'), value: 'suitable_for_singles' },
                        { text: $t('SearchBar.more.suitable_for_families'), value: 'suitable_for_families' },
                        { text: $t('SearchBar.more.suitable_for_dogs'), value: 'suitable_for_dogs' }
                    ]
                },
                {
                    header: $t('SearchBar.more.priceRange.header'),
                    options: [
                        { text: $t('SearchBar.more.price_range_50_99_per_person'), value: 'price_range_50_99' },
                        { text: $t('SearchBar.more.price_range_100_149_per_person'), value: 'price_range_100_149' },
                        { text: $t('SearchBar.more.price_range_150_199_per_person'), value: 'price_range_150_199' },
                        { text: $t('SearchBar.more.price_range_200_249_per_person'), value: 'price_range_200_249' },
                        { text: $t('SearchBar.more.price_range_250_299_per_person'), value: 'price_range_250_299' },
                        { text: $t('SearchBar.more.price_range_300_349_per_person'), value: 'price_range_300_349' },
                        { text: $t('SearchBar.more.price_range_350_399_per_person'), value: 'price_range_350_399' },
                        { text: $t('SearchBar.more.price_range_400_plus_per_person'), value: 'price_range_400_plus' }
                    ]
                },
                {
                    header: $t('SearchBar.more.topic.header'),
                    options: [
                        { text: $t('SearchBar.more.topic_wellness'), value: 'topic_wellness' },
                        { text: $t('SearchBar.more.topic_seaside'), value: 'topic_seaside' },
                        { text: $t('SearchBar.more.topic_activity'), value: 'topic_activity' },
                        { text: $t('SearchBar.more.topic_nature'), value: 'topic_nature' },
                        { text: $t('SearchBar.more.topic_adventure'), value: 'topic_adventure' },
                        { text: $t('SearchBar.more.topic_lakeside'), value: 'topic_lakeside' },
                        { text: $t('SearchBar.more.topic_citytrip'), value: 'topic_citytrip' },
                        { text: $t('SearchBar.more.topic_luxurytrip'), value: 'topic_luxurytrip' },
                        { text: $t('SearchBar.more.topic_mountains'), value: 'topic_mountains' },
                        { text: $t('SearchBar.more.topic_romantictrip'), value: 'topic_romantictrip' }
                    ]
                },
                {
                    header: $t('SearchBar.more.facilities.header'),
                    options: [
                        { text: $t('SearchBar.more.facilities_sauna'), value: 'facilities_sauna' },
                        { text: $t('SearchBar.more.facilities_pool'), value: 'facilities_pool' },
                        { text: $t('SearchBar.more.facilities_fitness'), value: 'facilities_fitness' },
                        { text: $t('SearchBar.more.facilities_restaurant'), value: 'facilities_restaurant' },
                        { text: $t('SearchBar.more.facilities_parking'), value: 'facilities_parking' },
                        { text: $t('SearchBar.more.facilities_publictransport'), value: 'facilities_publictransport' },
                        { text: $t('SearchBar.more.facilities_barrierfree'), value: 'facilities_barrierfree' }
                    ]
                },
                {
                    header: $t('SearchBar.more.stars.header'),
                    options: [
                        { text: $t('SearchBar.more.stars_3'), value: 'stars_3' },
                        { text: $t('SearchBar.more.stars_4'), value: 'stars_4' },
                        { text: $t('SearchBar.more.stars_5'), value: 'stars_5' }
                    ]
                }
            ],
            otherSelected: []
        };
    },
    props: {
        //filter options and query could have been grabbed from the $route object,
        //but having as a prop adds control whether to preselect
        //options and query or not
        filterOptions: Array,
        showDateSearchFields: Boolean
    },
    computed: {
        isMapRoute() {
            return this.$route.name === 'mapSearch';
        },
        availableFromDates() {
            let numberOfDaysUntil = this.initialSelectionRangeInDays;
            if (this.until) {
                const untilDate = parseGermanShortDateString(this.until);
                const diffTimeMs = Math.abs(untilDate - this.initialDate);
                numberOfDaysUntil = Math.ceil(diffTimeMs / (1000 * 60 * 60 * 24));
            }
            return generateDateList(this.initialDate, numberOfDaysUntil);
        },
        availableUntilDates() {
            const offset = this.from ? parseGermanShortDateString(this.from) : this.initialDate;
            const startDate = addDays(offset, 1);
            return generateDateList(startDate, this.initialSelectionRangeInDays);
        }
    },
    methods: {
        submitSearch(mapButton) {
            this.$emit('submit');

            const name = this.isMapRoute || mapButton ? 'mapSearch' : 'searchResult';
            const q = this.query;
            this.$router.push({
                name,
                query: {
                    ...this.$route.query,
                    filterOptions: [
                        ...this.durationSelected.map(duration => duration.value),
                        ...this.otherSelected.map(option => option.value)
                    ],
                    q,
                    from: this.from ? this.from : undefined,
                    until: this.until ? this.until : undefined
                },
                hash: '#searchBar'
            });
        },
        resetSearch() {
            this.from = '';
            this.until = '';
        },
        updateFrom(value) {
            this.from = value === null ? '' : value;
        },
        updateUntil(value) {
            this.until = value === null ? '' : value;
        }
    },
    watch: {
        filterOptions: {
            handler(filterOptions = []) {
                this.durationSelected = filterOptions.reduce((durationSelected, filterOption) => {
                    this.durationGroups.forEach(group => {
                        group.options.forEach(option => {
                            if (option.value === filterOption) {
                                durationSelected.push(option);
                            }
                        });
                    });
                    return durationSelected;
                }, []);

                this.otherSelected = filterOptions.reduce((otherSelected, filterOption) => {
                    this.otherFilterGroups.forEach(group => {
                        group.options.forEach(option => {
                            if (option.value === filterOption) {
                                otherSelected.push(option);
                            }
                        });
                    });
                    return otherSelected;
                }, []);
            },
            immediate: true
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints' as breakpoints;
@use '@/styles/theming' as *;

.search-bar {
    $self: &;
    background-color: var(--body-bg);

    &__section {
        border-bottom: 1px solid var(--border-color);
    }

    &__section + &__section {
        border-left: 1px solid var(--border-color);
    }

    display: flex;
    position: relative;
    flex-wrap: nowrap;

    &:focus,
    &:active,
    &:focus-visible {
        outline: none;
    }

    &__section {
        align-items: stretch;
        min-height: 80px;
        padding: 0 variables.$grid-padding;
        display: flex;
    }

    &__section-remaining {
        align-items: stretch;
    }

    &__filter-option {
        font-size: 14px;

        .icon-svg,
        ::v-deep .icon-svg {
            font-size: 18px;
            flex: 0 0 18px;
        }

        ::v-deep .prefix {
            margin-right: 10px;
            color: variables.$muted;
        }
    }

    &__other-option {
        overflow: hidden;
    }

    &__submit-button {
        margin: 10px 0;
        margin-left: 5px;
        line-height: 1;
        flex: 2;
        max-width: 170px;
        font-size: 1.2em;
        align-self: stretch;
        white-space: nowrap;
		color: var(--primary-text-color, var(--white))
    }

    &__map-search {
        padding: 5px;
        max-width: 110px;
        max-height: 60px;
        flex: 1;
    }

    &__map-hint {
        font-size: 12px;
        line-height: 14px;
        max-width: 80px;
        white-space: normal;
        margin-bottom: 0;
        align-self: center;
        padding-left: 5px;
        cursor: pointer;
        background: none;
        border: none;
        text-align: initial;
        outline: none;
    }

    &__icon-map-mobile {
        font-size: 30px;
    }

    .datepicker {
        display: flex;
        flex-wrap: nowrap;
        width: 11ch;
        align-items: center;

        &--empty {
            ::v-deep {
                .datepicker__input-wrapper {
                    padding-left: 25px;
                }
            }
        }

        &__label {
            font-size: 12px;
            position: absolute;
            top: -1em;
        }

        ::v-deep {
            .datepicker__input {
                border: none;
                outline: none;
                padding: 0;
                background-color: transparent; // prevents clear icon to appear over gray background confusing dismissible close action

                &::placeholder {
                    color: var(--body-color);
                }
            }

            .datepicker__icon.remove-icon {
                right: -10px;
            }

            .dismissible {
                width: 100%;
            }

            .calendar-icon {
                left: 0;
                right: auto;
            }
        }

        &:hover {
            ::v-deep {
                .datepicker__icon {
                    color: var(--primary);
                }
            }
        }
    }

    @include breakpoints.media-breakpoint-down(variables.$mobile-breakpoint-max) {
        &__duration {
            border-left: none !important;
        }
    }

    &--with-dates {
        @include breakpoints.media-breakpoint-up(xl) {
            #{$self}__other-option {
                flex: 0 0 16%;
                max-width: 16%;
            }
        }

        @include breakpoints.media-breakpoint-down(xs) {
            .search-bar__until {
                ::v-deep .calendar {
                    position: absolute;
                    left: -100%;
                }
            }
        }

        @include breakpoints.media-breakpoint-between(sm, lg) {
            #{$self}__button-text {
                display: none;
            }
        }

        @include breakpoints.media-breakpoint-up(xl) {
            #{$self}__section-remaining {
                flex: 0 0 auto;
                width: auto;
                max-width: 100%;
            }
        }
    }

    @include breakpoints.media-breakpoint-up(xl) {
        &__section-remaining {
            flex: 0 0 30%;
            max-width: 30%;
        }

        &__other-option {
            &--wide {
                flex: 0 0 38%;
                max-width: 38%;
            }
        }

        &__duration {
            flex: 0 0 20%;
            max-width: 20%;
        }
    }

    @include breakpoints.media-breakpoint-down(xs) {
        flex-wrap: wrap;

        &__section {
            border-left: none !important;
        }

        &__section-remaining {
            justify-content: center;
            border-bottom: none;
        }
    }

    @include breakpoints.media-breakpoint-only(sm) {
        &__button-text {
            display: none;
        }

        &__other-option {
            &--wide {
                flex: 0 0 48%;
                max-width: 48%;
            }
        }
    }

    @include breakpoints.media-breakpoint-down(sm) {
        flex-wrap: wrap;

        &__section {
            min-height: 60px;
        }

        &__submit-button {
            padding: 5px 10px;
            flex: 1;
        }

        &__filter-option ::v-deep {
            .dropdown-menu .dropdown-header {
                margin-top: 10px;
            }
        }
    }

    &__filter-option,
    &__map-hint {
        color: var(--body-color);
    }

    @include variables.ie {
        height: 80px;
    }
}
</style>
